import React, { useState, useEffect } from 'react';
import { Users, Target, Calendar, ArrowRight, TrendingUp, Briefcase } from 'lucide-react';
import { Link } from 'react-router-dom';
import { apiFetch } from '../../utils/api';

const CRMOverview: React.FC = () => {
    const [stats, setStats] = useState({
        totalLeads: 0,
        convertedLeads: 0,
        activeOpportunities: 0,
        totalContacts: 0
    });
    const [loading, setLoading] = useState(true);

    const token = localStorage.getItem('adminToken');

    useEffect(() => {
        const fetchStats = async () => {
            try {
                const [leadsRes, oppsRes, contactsRes] = await Promise.all([
                    apiFetch('/leads'),
                    apiFetch('/crm/opportunities'),
                    apiFetch('/crm/contacts')
                ]);

                if (leadsRes.ok) {
                    const leads = await leadsRes.json();
                    setStats(prev => ({
                        ...prev,
                        totalLeads: leads.length,
                        convertedLeads: leads.filter((l: any) => l.status === 'Converted').length
                    }));
                }
                if (oppsRes.ok) {
                    const opps = await oppsRes.json();
                    setStats(prev => ({ ...prev, activeOpportunities: opps.length }));
                }
                if (contactsRes.ok) {
                    const contacts = await contactsRes.json();
                    setStats(prev => ({ ...prev, totalContacts: contacts.length }));
                }
            } catch (err) {
                console.error('Error fetching CRM stats:', err);
            } finally {
                setLoading(false);
            }
        };
        fetchStats();
    }, [token]);

    const statCards = [
        { label: 'Total Leads', value: stats.totalLeads, icon: <Users />, color: 'bg-blue-500', link: '/admin/leads' },
        { label: 'Converted Leads', value: stats.convertedLeads, icon: <TrendingUp />, color: 'bg-green-500', link: '/admin/leads' },
        { label: 'Opportunities', value: stats.activeOpportunities, icon: <Target />, color: 'bg-purple-500', link: '/admin/crm/opportunities' },
        { label: 'Total Contacts', value: stats.totalContacts, icon: <Briefcase />, color: 'bg-orange-500', link: '/admin/crm/contacts' },
    ];

    if (loading) {
        return <div className="flex items-center justify-center h-64">Loading statistics...</div>;
    }

    return (
        <div className="space-y-8">
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
                {statCards.map((card, i) => (
                    <div key={i} className="bg-white p-6 rounded-2xl shadow-sm border border-gray-100 flex items-center gap-4 hover:shadow-md transition-shadow">
                        <div className={`${card.color} p-4 rounded-xl text-white`}>
                            {React.cloneElement(card.icon as any, { size: 24 })}
                        </div>
                        <div>
                            <p className="text-sm text-gray-500 font-medium">{card.label}</p>
                            <h3 className="text-2xl font-bold text-gray-800">{card.value}</h3>
                        </div>
                    </div>
                ))}
            </div>

            <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
                {/* Quick Actions */}
                <div className="bg-white p-8 rounded-2xl shadow-sm border border-gray-100">
                    <h3 className="text-xl font-bold text-gray-800 mb-6">Quick CRM Actions</h3>
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                        <Link to="/admin/crm/opportunities" className="p-4 rounded-xl border border-gray-100 hover:border-blue-500 hover:bg-blue-50 transition-all flex items-center justify-between group">
                            <div className="flex items-center gap-3">
                                <Target className="text-blue-500" />
                                <span className="font-medium">New Opportunity</span>
                            </div>
                            <ArrowRight size={18} className="text-gray-400 group-hover:text-blue-500 transform group-hover:translate-x-1 transition-all" />
                        </Link>
                        <Link to="/admin/crm/contacts" className="p-4 rounded-xl border border-gray-100 hover:border-blue-500 hover:bg-blue-50 transition-all flex items-center justify-between group">
                            <div className="flex items-center gap-3">
                                <Users className="text-blue-500" />
                                <span className="font-medium">Direct Contact Add</span>
                            </div>
                            <ArrowRight size={18} className="text-gray-400 group-hover:text-blue-500 transform group-hover:translate-x-1 transition-all" />
                        </Link>
                    </div>
                </div>

                {/* Pipeline Health */}
                <div className="bg-white p-8 rounded-2xl shadow-sm border border-gray-100">
                    <h3 className="text-xl font-bold text-gray-800 mb-6">Upcoming Activities</h3>
                    <div className="flex flex-col items-center justify-center p-8 text-gray-400">
                        <Calendar size={48} className="mb-4 opacity-20" />
                        <p>No activities scheduled for today</p>
                        <button className="mt-4 text-blue-500 font-semibold hover:underline">Schedule Activity</button>
                    </div>
                </div>
            </div>
        </div>
    );
};

export default CRMOverview;
